import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Flex, InputRef } from "antd";
import { TeamOutlined, CalendarOutlined, LockOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { getInstructorAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { AnalyticsRange, CustomDayRange, InstructorAnalyticsRow } from "shared/types";
import PATHS from "shared/constants/paths";
import { getYtdDayRange } from "shared/utils/dayjs";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { instrukturSpec } from "pdf/reports/specs/archetype1";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

const InstructorPerformancePage = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getInstructorAnalytics(range, customRange);

  const columns: ReportColumn<InstructorAnalyticsRow>[] = [
    reportColumn<InstructorAnalyticsRow>("instructor", {
      dataIndex: "instructor_name",
      search: getColumnSearchProps<InstructorAnalyticsRow, "instructor_name">({
        config: "instructor_name",
        searchInput,
      }),
    }),
    reportColumn<InstructorAnalyticsRow>("session_contribution", {
      dataIndex: "session_contribution_pct",
      info: () => translate("analyticsPage.instructor.col_session_contribution_info"),
      sorter: (a, b) => a.session_contribution_pct - b.session_contribution_pct,
      // Matches the order the server already returns, so the arrow shows the
      // sort that is actually applied instead of leaving the table looking
      // unsorted.
      defaultSortOrder: "descend",
    }),
    reportColumn<InstructorAnalyticsRow>("sessions_completed", {
      dataIndex: "sessions_completed",
      sorter: (a, b) => a.sessions_completed - b.sessions_completed,
    }),
    reportColumn<InstructorAnalyticsRow>("open_sessions", {
      dataIndex: "open_sessions",
      sorter: (a, b) => a.open_sessions - b.open_sessions,
    }),
    reportColumn<InstructorAnalyticsRow>("private_sessions", {
      dataIndex: "private_sessions",
      sorter: (a, b) => a.private_sessions - b.private_sessions,
    }),
    reportColumn<InstructorAnalyticsRow>("bookings", {
      dataIndex: "bookings",
      info: () => translate("analyticsPage.instructor.col_bookings_info"),
      sorter: (a, b) => a.bookings - b.bookings,
    }),
    reportColumn<InstructorAnalyticsRow>("unique_clients", {
      dataIndex: "unique_clients",
      info: () => translate("analyticsPage.instructor.col_unique_clients_info"),
      sorter: (a, b) => a.unique_clients - b.unique_clients,
    }),
  ];

  const pageCount = useReportPageCount(instrukturSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.instructor.report_performance")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-instruktur"
            params={
              customRange ? { from_date: customRange.from, to_date: customRange.to } : { range }
            }
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table.length,
                },
                fileName: `spott-performa-instruktur-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-instruktur",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<TeamOutlined />}
            title={translate("analyticsPage.instructor.kpi_total_instructors")}
            value={data?.total_instructors.value ?? 0}
          />
          <MetricCard
            icon={<CalendarOutlined />}
            title={translate("analyticsPage.instructor.kpi_open_sessions")}
            value={data?.open_sessions.value ?? 0}
          />
          <MetricCard
            icon={<LockOutlined />}
            title={translate("analyticsPage.instructor.kpi_private_sessions")}
            value={data?.private_sessions.value ?? 0}
          />
        </Flex>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<InstructorAnalyticsRow>
            rowKey="instructor_id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsInstructorPerformance"
            onSortChange={onSortChange}
            onRow={(row) => ({
              onClick: () => navigate(`${PATHS.STAFF}/${row.instructor_id}?tab=2`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default InstructorPerformancePage;
